import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSectionTabs } from '../context/SectionTabs';

/**
 * A sidebar section rendered as one page, with its tabs in the HEADER.
 *
 * Four sections used to be four-to-seven sidebar entries each, which meant a
 * full page load to answer one question about one thing.
 *
 * The tabs live in the header because they are navigation, and navigation
 * belongs in the chrome: below a page title they read as content and cost two
 * rows of vertical space before anything useful starts.
 *
 * The page components are mounted UNCHANGED — each still owns its own
 * fetching and state — so this only decides which is on screen. The active tab
 * is in the URL, so a reload stays put and a link to one tab is still a link
 * to it.
 */
const TabbedSection = ({ title, basePath, tabs }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setSection, clearSection } = useSectionTabs();

    const active = useMemo(() => {
        const requested = new URLSearchParams(location.search).get('tab');
        return tabs.some((t) => t.key === requested) ? requested : tabs[0].key;
    }, [location.search, tabs]);

    // Publish to the header, and take them down again on the way out — a
    // section's tabs must not linger above a page they do not belong to.
    useEffect(() => {
        setSection({
            title,
            basePath,
            active,
            tabs: tabs.map(({ key, label }) => ({ key, label })),
        });
        return () => clearSection();
    }, [title, basePath, active, tabs, setSection, clearSection]);

    // Only the active pane is rendered. Mounting all seven of Website & App
    // would fire seven requests to show one list, and the tab state lives in
    // the URL rather than in a component, so nothing is lost by unmounting.
    const Current = useMemo(
        () => (tabs.find((t) => t.key === active) ?? tabs[0]).Component,
        [tabs, active],
    );

    // `navigate` is unused once the tabs moved out, but keeping the signature
    // means a caller can still be handed one if a section ever needs its own.
    void navigate;

    return <Current />;
};

export default TabbedSection;
